import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Pill, Save } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function Consignations() {
  const { user } = useAuth();
  const canDelete = user?.role === "patron" || user?.role === "associe";
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [form, setForm] = useState({ pharmacy_name: "", product_id: "", qty_deposited: "", unit_price: "" });

  const inputCls = "w-full border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

  const load = () => api.get("/consignations").then(({ data }) => setData(data)).catch(() => setData({ items: [], pharmacies: [] }));
  useEffect(() => {
    load();
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    setForm((f) => ({ ...f, product_id: id, unit_price: p ? String(p.price) : f.unit_price }));
  };

  const add = async () => {
    if (!form.pharmacy_name.trim()) return toast.error("Nom de la pharmacie requis");
    if (!form.product_id) return toast.error("Choisissez un produit");
    if (!Number(form.qty_deposited) || Number(form.qty_deposited) <= 0) return toast.error("Quantité invalide");
    setSaving(true);
    try {
      const p = products.find((x) => x.id === form.product_id);
      await api.post("/consignations", {
        pharmacy_name: form.pharmacy_name.trim(), product_id: form.product_id,
        product_name: p?.name || "", qty_deposited: Number(form.qty_deposited), unit_price: Number(form.unit_price) || 0,
        pharmacy_margin_pct: Number(form.pharmacy_margin_pct) || 0,
      });
      toast.success("Dépôt enregistré");
      setForm({ pharmacy_name: "", product_id: "", qty_deposited: "", unit_price: "", pharmacy_margin_pct: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveLine = async (c) => {
    const e = edits[c.id] || {};
    try {
      await api.patch(`/consignations/${c.id}`, {
        qty_sold: e.qty_sold !== undefined ? Number(e.qty_sold) : c.qty_sold,
        qty_returned: e.qty_returned !== undefined ? Number(e.qty_returned) : c.qty_returned,
      });
      toast.success("Mis à jour");
      setEdits((s) => { const n = { ...s }; delete n[c.id]; return n; });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette ligne ?")) return;
    try { await api.delete(`/consignations/${id}`); toast.success("Supprimée"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="consignations-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink flex items-center gap-2"><Pill className="w-7 h-7 text-brand" /> Dépôts en pharmacie</h1>
        <p className="text-brand-muted mt-1">Suivez les produits déposés en pharmacie, ce qui est vendu et le montant à récupérer.</p>
      </div>

      {/* Résumé par pharmacie */}
      {data?.pharmacies?.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.pharmacies.map((p) => (
            <div key={p.pharmacy_name} className="bg-white border border-brand-line rounded-2xl p-4" data-testid={`pharmacy-summary-${p.pharmacy_name}`}>
              <p className="font-heading font-bold text-brand-ink">{p.pharmacy_name}</p>
              <p className="text-xs text-brand-muted mt-1">{p.lines} produit(s) déposé(s)</p>
              <p className="text-sm mt-2">Valeur vendue : <b className="text-brand-ink">{fcfa(p.sale_value)}</b></p>
              {p.pharmacy_share > 0 && <p className="text-sm">Part pharmacie : <b className="text-red-500">-{fcfa(p.pharmacy_share)}</b></p>}
              <p className="text-sm">À encaisser : <b className="text-amber-700">{fcfa(p.to_collect)}</b></p>
            </div>
          ))}
        </div>
      )}

      {/* Nouveau dépôt */}
      <div className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
        <h2 className="font-heading font-bold text-brand-ink mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-brand" /> Nouveau dépôt</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input value={form.pharmacy_name} onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })} placeholder="Nom de la pharmacie" className={inputCls} data-testid="consignation-pharmacy" />
          <select value={form.product_id} onChange={(e) => pickProduct(e.target.value)} className={inputCls} data-testid="consignation-product">
            <option value="">Choisir un produit…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input type="number" value={form.qty_deposited} onChange={(e) => setForm({ ...form, qty_deposited: e.target.value })} placeholder="Quantité déposée" className={inputCls} data-testid="consignation-qty" />
          <input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="Prix unitaire (FCFA)" className={inputCls} data-testid="consignation-price" />
          <input type="number" value={form.pharmacy_margin_pct} onChange={(e) => setForm({ ...form, pharmacy_margin_pct: e.target.value })} placeholder="% pharmacie (ex: 10)" className={inputCls} data-testid="consignation-margin" />
        </div>
        <p className="text-xs text-brand-muted mt-2">💡 Le « % pharmacie » est la marge que la pharmacie garde. Ex : produit à 10 000 avec 10 % → vous encaissez 9 000, la pharmacie garde 1 000.</p>
        <button onClick={add} disabled={saving} data-testid="add-consignation-button" className="mt-4 bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Enregistrer le dépôt
        </button>
      </div>

      {/* Liste */}
      <div className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
        <h2 className="font-heading font-bold text-brand-ink mb-4">Produits en consignation</h2>
        {data === null ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
          : data.items.length === 0 ? <p className="text-sm text-brand-muted">Aucun dépôt enregistré.</p>
          : (
            <div className="space-y-2">
              {data.items.map((c) => {
                const e = edits[c.id] || {};
                return (
                  <div key={c.id} data-testid={`consignation-row-${c.id}`} className="border border-brand-line rounded-xl px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-brand-ink text-sm">{c.product_name} <span className="text-brand-muted font-normal">· {c.pharmacy_name}</span></p>
                        <p className="text-xs text-brand-muted">Déposé {c.qty_deposited} · Reste {c.remaining} · Vendu {fcfa(c.sale_value)}{c.pharmacy_margin_pct > 0 ? ` · Pharmacie ${c.pharmacy_margin_pct}% (-${fcfa(c.pharmacy_share)})` : ""} · À encaisser {fcfa(c.amount_to_collect)}</p>
                      </div>
                      {canDelete && <button onClick={() => remove(c.id)} data-testid={`delete-consignation-${c.id}`} className="text-red-500 hover:bg-red-50 rounded-lg p-2"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                    <div className="flex flex-wrap items-end gap-3 mt-3">
                      <div>
                        <label className="text-[11px] text-brand-muted">Vendu</label>
                        <input type="number" defaultValue={c.qty_sold} onChange={(ev) => setEdits((s) => ({ ...s, [c.id]: { ...s[c.id], qty_sold: ev.target.value } }))} data-testid={`sold-input-${c.id}`} className="w-24 border border-brand-line rounded-lg px-2 py-1.5 text-sm block" />
                      </div>
                      <div>
                        <label className="text-[11px] text-brand-muted">Retourné</label>
                        <input type="number" defaultValue={c.qty_returned} onChange={(ev) => setEdits((s) => ({ ...s, [c.id]: { ...s[c.id], qty_returned: ev.target.value } }))} data-testid={`returned-input-${c.id}`} className="w-24 border border-brand-line rounded-lg px-2 py-1.5 text-sm block" />
                      </div>
                      {(e.qty_sold !== undefined || e.qty_returned !== undefined) && (
                        <button onClick={() => saveLine(c)} data-testid={`save-consignation-${c.id}`} className="bg-brand text-white rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Enregistrer</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
